/**
 * AtlasPuddleModule — rain that runs downhill and pools into real puddles (shader gaps program R6, 2026-09-30; the
 * owner's "shallow-water simulation … for forming puddles when it rains" and "the combination of static puddles and
 * shallow water simulation"). Read `lib/puddle_sim.ts` for the design; this is the game's driver of the shared
 * `PuddleRuntime` (`lib/puddle_runtime.ts`).
 *
 * Every frame (RenderSub 10 — after the roof map's bake at 9, before the render): the decision (`puddleSimDecision`:
 * the tier's `puddleSim`, WebGPU, no XR, the wet clock on, the kill switch `atlas:puddles`), the Commons check, the
 * window follows the camera by whole cells, a time-budgeted slice of the bed is built from THE DRAWN GROUND
 * (EnvironmentModule.puddleGround: `meshHeight` over the resident chunks' own grids, their rules for the ground class,
 * the wet mask and the ruts; `structure_solids` for walls; `waterStillAt` for drains), a changed chunk is queued again,
 * THE ONE WET CLOCK's rates are read (never stepped), the kernels step, and the channel the ground reads is kept in
 * the render frame (the FP64 rebase moves it every frame, never the water).
 *
 * NEVER AUTHORITATIVE: buoyancy, swimming, footing, vehicles and the server know nothing of this water.
 *
 * Lever `window.__puddles()` — the decision, the window, the build, the step (dt, substeps, drops), the clock's rates,
 * and the last readback: volume, wet cells, max depth, the ledger and the budget residual.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { activePreset } from '../lib/quality_tiers.js';
import { wetClock } from '../lib/wet_state.js';
import { shelterExposure } from '../lib/rain_shelter.js';
import { waterStillAt } from '../lib/water_fx.js';
import { structureSolidsNear, type StructureSolid } from '../lib/structure_solids.js';
import { meshHeight } from '../lib/terrain_stamp.js';
import { renderAnchorX, renderAnchorZ } from '../lib/local_origin.js';
import { CHUNK_SIZE } from '../config.js';
import {
  puddleSimDecision, setPuddleBackend, puddlePatchFor, ratesFromClock, puddleKilled, type PuddleGround, type PuddleReason,
} from '../lib/puddle_sim.js';
import { ensurePuddleChannel, puddleChannel } from '../lib/puddle_layer.js';
import { PuddleRuntime, chunkRulesAt } from '../lib/puddle_runtime.js';
import type { EnvironmentModule } from './EnvironmentModule.js';

/** The bed build's slice of a frame (ms). */
const BUILD_BUDGET_MS = 2.5;
/** A diagnostics readback this often (s) while the lever was asked in the last minute. */
const READ_EVERY_S = 2;

export class AtlasPuddleModule implements IModule {
  readonly name = 'AtlasPuddle';
  private app!: App;
  private env: EnvironmentModule | null = null;
  private rt: PuddleRuntime | null = null;
  private lastMs = 0;
  private reason: PuddleReason | 'off-world' | 'no-ground' = 'off-tier';
  private readonly chunkSeen = new Map<string, THREE.Object3D | null>();
  private solids: StructureSolid[] = [];
  private solidsAt = 0;
  private leverAskedMs = -1e9;
  private readClock = 0;
  private readonly cam = new THREE.Vector3();
  private frames = 0;

  init(app: App): void {
    this.app = app;
    const native = (app.renderer as unknown as { native?: { backend?: { isWebGPUBackend?: boolean; device?: { limits?: { maxStorageBuffersInFragmentStage?: number } } } } }).native;
    // THE BACKEND FLAG LAW — never the class name. The ground reads the patch through a storage buffer in the FRAGMENT
    // stage: a device in WebGPU compatibility mode may allow none there (the limit is absent on a core device = allowed)
    const fragStorage = native?.backend?.device?.limits?.maxStorageBuffersInFragmentStage;
    setPuddleBackend(native?.backend?.isWebGPUBackend === true && (fragStorage === undefined || fragStorage >= 1));
    app.world.addSystem(makeSystem('AtlasPuddle', Phase.RenderSub, 10, () => this.tick()));
    (globalThis as unknown as { __puddles?: () => unknown }).__puddles = () => { this.leverAskedMs = performance.now(); return this.diag(); };
  }

  private renderer(): THREE.WebGPURenderer & { compute(n: unknown): unknown; getArrayBufferAsync(a: unknown): Promise<ArrayBuffer> } {
    return (this.app.renderer as unknown as { native: never }).native;
  }

  private park(reason: typeof this.reason): void {
    this.reason = reason;
    (puddleChannel.uOn as unknown as { value: number }).value = 0;
  }

  private tick(): void {
    const now = performance.now();
    const dt = this.lastMs ? Math.min(1, (now - this.lastMs) / 1000) : 0;
    this.lastMs = now;
    const r = this.renderer();
    const renderer = this.app.renderer as unknown as { paused?: boolean };
    if (renderer.paused) return;
    const xr = !!(r as unknown as { xr?: { isPresenting?: boolean } }).xr?.isPresenting;
    const dec = puddleSimDecision(xr);
    if (!dec.on) { this.park(dec.reason); return; }
    const onWorld = !atlas.spaceFlight && atlas.currentWorldId != null && atlas.currentWorldId === atlas.commonsWorldId && !atlas.loadingGate;
    if (!onWorld) { this.park('off-world'); return; }
    this.env ??= (this.app.getModule?.('Environment') as EnvironmentModule | undefined) ?? null;
    const pg = this.env?.puddleGround() ?? null;
    if (!pg) { this.park('no-ground'); return; }
    const cfg = puddlePatchFor(activePreset() as never);
    if (!cfg) { this.park('off-tier'); return; }
    // the channel is the ground's (it may have been created by a ground build already) — one buffer per size
    const attr = ensurePuddleChannel(cfg.n);
    if (!this.rt || this.rt.n !== cfg.n || this.rt.cell !== cfg.cell || this.rt.gpu.lookAttr !== attr) {
      this.rt?.dispose();
      this.rt = new PuddleRuntime(cfg, attr, (p) => shelterExposure(p));
      this.chunkSeen.clear();
    }
    const rt = this.rt;
    if (!rt.prime(r)) { this.park('on'); return; } // THE WARM: one pipeline a frame
    this.reason = 'on';
    // the camera, ABSOLUTE (render + the origin)
    const camObj = (this.app.renderer as unknown as { cameras?: { main?: THREE.Camera } }).cameras?.main;
    const sp = this.app.world.scene.position;
    if (camObj) this.cam.setFromMatrixPosition(camObj.matrixWorld).sub(sp); else this.cam.set(atlas.localX, 0, atlas.localZ);
    const seed = pg.seed;
    const height = (x: number, z: number): number => meshHeight(seed, x, z);
    const moved = rt.follow(r, this.cam.x, this.cam.z, height);
    if (moved !== 'still') this.chunkSeen.clear();
    this.watchChunks(pg);
    // the solids near the window (once a second, or after a move)
    if (moved !== 'still' || now - this.solidsAt > 1000) {
      this.solids = structureSolidsNear(rt.win.centerX, rt.win.centerZ, rt.win.side * 0.75);
      this.solidsAt = now;
    }
    const ground: PuddleGround = {
      height,
      rules: (x, z) => chunkRulesAt(pg.chunk(Math.floor(x / CHUNK_SIZE), Math.floor(z / CHUNK_SIZE)), x, z, CHUNK_SIZE),
      still: (x, z) => waterStillAt(x, z),
      solid: (x, z, y) => this.solidAt(x, z, y),
    };
    rt.build(ground, BUILD_BUDGET_MS);
    const rates = ratesFromClock(wetClock);
    rt.step(r, dt, rates, renderAnchorX(), renderAnchorZ(), sp.y);
    (puddleChannel.uOn as unknown as { value: number }).value = 1; // pending cells hand back per cell (look.w)
    this.frames++;
    // diagnostics readback while someone is looking
    this.readClock += dt;
    if (now - this.leverAskedMs < 60_000 && this.readClock >= READ_EVERY_S) { this.readClock = 0; void rt.read(r); }
  }

  /** A resident chunk under the window changed (a re-LOD, a restamp, streamed in) → rebuild its cells. */
  private watchChunks(pg: NonNullable<ReturnType<EnvironmentModule['puddleGround']>>): void {
    const rt = this.rt!; const w = rt.win;
    const c0 = Math.floor(w.minX / CHUNK_SIZE), c1 = Math.floor((w.minX + w.side) / CHUNK_SIZE);
    const r0 = Math.floor(w.minZ / CHUNK_SIZE), r1 = Math.floor((w.minZ + w.side) / CHUNK_SIZE);
    for (let cz = r0; cz <= r1; cz++) for (let cx = c0; cx <= c1; cx++) {
      const key = `${cx},${cz}`;
      const m = pg.chunk(cx, cz)?.mesh ?? null;
      if (!this.chunkSeen.has(key)) { this.chunkSeen.set(key, m); continue; }
      if (this.chunkSeen.get(key) === m) continue;
      this.chunkSeen.set(key, m);
      rt.invalidate(cx * CHUNK_SIZE, cz * CHUNK_SIZE, (cx + 1) * CHUNK_SIZE, (cz + 1) * CHUNK_SIZE);
    }
  }

  /** A building stands on (x, z) at this ground height (three's yaw convention — structure_solids' toLocal). */
  private solidAt(x: number, z: number, y: number): boolean {
    for (const s of this.solids) {
      if (y < s.base - 0.5 || y > s.top) continue;
      const dx = x - s.x, dz = z - s.z, c = Math.cos(s.yaw), sn = Math.sin(s.yaw);
      if (Math.abs(dx * c - dz * sn) <= s.hx && Math.abs(dx * sn + dz * c) <= s.hz) return true;
    }
    return false;
  }

  private diag(): Record<string, unknown> {
    const rt = this.rt;
    const dec = puddleSimDecision();
    return {
      on: this.reason === 'on', reason: this.reason, decision: dec, killSwitch: puddleKilled(),
      tier: activePreset().puddleSim ?? 'off',
      patch: rt ? { n: rt.n, cell: rt.cell, side: rt.win.side, minX: rt.win.minX, minZ: rt.win.minZ, refY: rt.refY, primed: rt.primed, pending: rt.pending } : null,
      build: rt ? { ...rt.stats } : null,
      step: rt ? { dt: rt.gpu.dt, ...rt.gpu.stats } : null,
      rates: ratesFromClock(wetClock),
      channel: { n: puddleChannel.n, grounds: puddleChannel.grounds, on: (puddleChannel.uOn as unknown as { value: number }).value },
      last: rt?.last ? { ageMs: performance.now() - rt.last.at, volume: rt.last.volume, wetCells: rt.last.wetCells, maxDepth: rt.last.maxDepth, ledger: rt.last.ledger, residual: rt.last.residual } : null,
      frames: this.frames,
      /** Force a readback now (resolves with the stats). */
      readNow: async () => (this.rt ? this.rt.read(this.renderer()) : null),
      /** The depth (m) at an absolute point from the last readback. */
      depthAt: (x: number, z: number) => this.rt?.depthAt(x, z) ?? null,
    };
  }

  dispose(): void { this.rt?.dispose(); this.rt = null; }
}
